"use strict";
/**
 * Copyright © 2019 - Present, Vamstar Limited
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or
 * without modification, are not permitted.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LegendUtils = void 0;
const legend_marker_enum_1 = require("../common/enums/legend-marker-enum");
const types_1 = require("../lot/types");
const addMonths_1 = __importDefault(require("date-fns/addMonths"));
const format_1 = __importDefault(require("date-fns/format"));
const isAfter_1 = __importDefault(require("date-fns/isAfter"));
const isSameDay_1 = __importDefault(require("date-fns/isSameDay"));
const isWithinInterval_1 = __importDefault(require("date-fns/isWithinInterval"));
const startOfDay_1 = __importDefault(require("date-fns/startOfDay"));
const date_utils_1 = require("./date-utils");
/**
 * Can be used across flat-lot, calendar and all search to show legend marker
 */
class LegendUtils {
    /**
     * Checks if the date matches criteria and returns LegendProps to show the right legend marker
     * @param searchResultUnion - Union of flat lot and entity
     * @returns ILegendMarkerProps | null
     */
    static getDateLegendMarkerProps(searchResultUnion) {
        const closeDate = date_utils_1.DateUtils.getCloseDate(searchResultUnion);
        const openDate = date_utils_1.DateUtils.getOpenDate(searchResultUnion);
        let markerProps = LegendUtils.getLegendMarkerProps(null, openDate, legend_marker_enum_1.LegendMarkerEnum.OPEN);
        if (LegendUtils.isAwardOrExTransparency(searchResultUnion)) {
            markerProps = LegendUtils.getLegendMarkerProps(null, openDate, legend_marker_enum_1.LegendMarkerEnum.MISSED);
        }
        else if (closeDate) {
            markerProps = LegendUtils.getOpenAndCloseDateMarkerProps(closeDate, openDate);
        }
        return markerProps;
    }
    /**
     * Let us whether the search result union is AWARD or EX-TRANSPARENCY
     * @param searchResultUnion - Union of flat lot and entity
     * @returns boolean
     */
    static isAwardOrExTransparency(searchResultUnion) {
        if (searchResultUnion.noticeType) {
            const valueOfNoticeType = (0, types_1.getValueOfNoticeType)(searchResultUnion.noticeType);
            return valueOfNoticeType === types_1.FlatLotNoticeType.AWARD || valueOfNoticeType === types_1.FlatLotNoticeType.EX_TRANSPARENCY;
        }
        return false;
    }
    /**
     * Gets open date - close date marker props
     * @param closeDate - Contract close date
     * @param openDate - Contract open date
     * @returns - Legend marker props
     */
    static getOpenAndCloseDateMarkerProps(closeDate, openDate) {
        const currentDate = (0, startOfDay_1.default)(new Date());
        const isContractOpen = (0, isAfter_1.default)(closeDate, currentDate);
        if (isContractOpen) {
            const sameMonth = (0, isWithinInterval_1.default)(closeDate, { start: currentDate, end: (0, addMonths_1.default)(currentDate, 1) });
            if (sameMonth) {
                return LegendUtils.getLegendMarkerProps(closeDate, openDate, legend_marker_enum_1.LegendMarkerEnum.ENDING);
            }
            return LegendUtils.getLegendMarkerProps(closeDate, openDate, legend_marker_enum_1.LegendMarkerEnum.OPEN);
        }
        return LegendUtils.getLegendMarkerProps(closeDate, openDate, legend_marker_enum_1.LegendMarkerEnum.MISSED);
    }
    /**
     * Gets the date with right marker
     * @param closeDate - Contract close date
     * @param color - color of the marker
     * @param shade - shade of the marker
     * @param openDate - Contract open date
     * @returns ILegendMarkerProps
     */
    static getLegendMarkerProps(closeDate, openDate, marker) {
        const label = LegendUtils.formatDateLabel(closeDate, openDate);
        return label ? { label, marker } : null;
    }
    /**
     * Gets the formatted date label (openDate - closeDate)
     * @param closeDate - Date when contract is ending
     * @param openDate - When contract was opened
     * @returns date label
     */
    static formatDateLabel(closeDate, openDate) {
        let formattedLebel = LegendUtils.formatDate(openDate);
        formattedLebel = closeDate ? `${formattedLebel} - ` : formattedLebel;
        formattedLebel += `${LegendUtils.formatDate(closeDate)}`;
        return formattedLebel;
    }
    /**
     * Formats the date into dd MMMM yyyy format (11 April 2021)
     * @param date - date to be formatted
     * @returns formatted date as string
     */
    static formatDate(date) {
        return date ? (0, format_1.default)(date, 'dd MMMM	yyyy') : '';
    }
    /**
     * Gets the calendar legend marker
     * @param aggregationKey - Aggregation key
     * @param date - date of calendar month
     * @param label - Number of contracts
     * @returns - Legend marker
     */
    static getCalendarMonthLegendMarkerProps(aggregationKey, date, label) {
        const legendMarker = LegendUtils.getMonthLegendMarkerProps({ [aggregationKey]: date });
        if (legendMarker) {
            legendMarker.label = label;
            return legendMarker;
        }
        return null;
    }
    /**
     * Checks if the date matches criteria and returns LegendProps to show the right legend marker
     * Checks if the renewal date is present
     * @param searchResultUnion - Union of flat lot and entity
     * @returns ILegendMarkerProps | null
     */
    static getMonthLegendMarkerProps(searchResultUnion) {
        const renewalDate = date_utils_1.DateUtils.getRenewalDate(searchResultUnion);
        if (renewalDate) {
            return LegendUtils.getLegendMarkerProps(null, renewalDate, legend_marker_enum_1.LegendMarkerEnum.RECURRING);
        }
        return LegendUtils.getDateLegendMarkerProps(searchResultUnion);
    }
    /**
     * checks if estimated open submission date is valid expected date
     * @param selectedDate - User selected date
     * @param closeDate - close submission date
     * @param estimatedOpenDate - estimated open submission date
     * @returns - boolean
     */
    static isValidExpectedDate(selectedDate, closeDate, estimatedOpenDate) {
        if ((0, isAfter_1.default)(selectedDate, new Date()) || (0, isSameDay_1.default)(selectedDate, new Date())) {
            if ((0, isAfter_1.default)(estimatedOpenDate, closeDate) &&
                (0, isAfter_1.default)(estimatedOpenDate, selectedDate) &&
                !(0, isSameDay_1.default)(selectedDate, closeDate)) {
                return true;
            }
        }
        return false;
    }
    /**
     * Gets calendar legend marker props
     * @param selectedDate - Selected date where user is on
     * @param searchResultUnion - ISearchResultUnion
     * @returns ILegendMarkerProps
     */
    static getCalendarLegendMarkerProps(selectedDate, searchResultUnion) {
        const { closeSubmissionDate, estimatedOpenSubmissionDate, publishedDate } = searchResultUnion;
        if (closeSubmissionDate && estimatedOpenSubmissionDate) {
            const closeDate = new Date(closeSubmissionDate);
            const estimatedOpenDate = new Date(estimatedOpenSubmissionDate);
            if (LegendUtils.isValidExpectedDate(selectedDate, closeDate, estimatedOpenDate)) {
                return LegendUtils.getLegendMarkerProps(null, estimatedOpenDate, legend_marker_enum_1.LegendMarkerEnum.RECURRING);
            }
        }
        else if (publishedDate && estimatedOpenSubmissionDate) {
            const estimatedOpenDate = new Date(estimatedOpenSubmissionDate);
            return LegendUtils.getLegendMarkerProps(null, estimatedOpenDate, legend_marker_enum_1.LegendMarkerEnum.RECURRING);
        }
        return LegendUtils.getDateLegendMarkerProps(searchResultUnion);
    }
}
exports.LegendUtils = LegendUtils;
//# sourceMappingURL=data:application/json;base64,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